import React from 'react';
import { motion } from 'framer-motion';
import { Gamepad2, Utensils, Star, Sparkles, Calendar, Baby, GlassWater, Car } from 'lucide-react';
import { useLang } from '../context/LanguageContext';

const ICONS = [Gamepad2, Utensils, Star, Sparkles, Calendar, Baby, GlassWater, Car];

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function AboutConcept() {
  const { t } = useLang();
  const a = t.about;

  return (
    <section id="about" className="py-32 md:py-40 relative overflow-hidden" style={{ background: '#0A0B0F' }}>
      {/* Background image with overlay */}
      <div className="section-bg">
        <img src="/img/photo_3_2026-03-14_14-38-32.jpg" alt="" />
      </div>

      {/* Enhanced orb */}
      <div
        className="orb-purple absolute"
        style={{ width: 700, height: 700, top: '-15%', left: '-8%', opacity: 0.12, zIndex: 1 }}
      />
      <div
        className="orb-gold absolute"
        style={{ width: 500, height: 500, bottom: '-10%', right: '-5%', opacity: 0.1, zIndex: 1 }}
      />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        {/* Enhanced Header */}
        <motion.div {...fadeUp} className="mb-20 max-w-4xl">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-[#D4AF37] to-transparent" />
            <span className="text-[#D4AF37] text-sm uppercase tracking-[0.2em] font-bold">Concept</span>
          </div>
          <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-white mb-8 font-heading leading-[0.95]" style={{ fontFamily: "'Sora', sans-serif", textShadow: '0 2px 30px rgba(0,0,0,0.5)' }}>
            {a.title}
          </h2>
          <p className="text-[#E5E7EB] text-xl md:text-2xl leading-relaxed mb-4 font-medium">{a.desc1}</p>
          <p className="text-[#BFC3C9] text-lg md:text-xl leading-relaxed">{a.desc2}</p>
        </motion.div>

        {/* Premium Feature Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {a.features.map((feature, i) => {
            const Icon = ICONS[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-80px' }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="glass-premium rounded-3xl p-7 card-premium group cursor-default"
                data-testid={`about-feature-${i}`}
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-all duration-500 group-hover:scale-110 group-hover:rotate-6"
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(212,175,55,0.15) 0%, rgba(212,175,55,0.05) 100%)', 
                    border: '1px solid rgba(212,175,55,0.25)',
                    boxShadow: '0 8px 24px rgba(212,175,55,0.1)'
                  }}
                >
                  <Icon size={24} className="text-[#D4AF37]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-3 font-heading leading-tight">
                  {feature.title}
                </h3>
                <p className="text-[#9CA3AF] text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
